import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { connect } from 'react-redux'

import {
  GalleryControlInterface,
  GalleryItemInterface,
  HouseAndLandPackageOptionType,
  LotGallery as LotGalleryInterface,
  PlayerControlInterface,
  PlayerState,
  ProjectIdentity,
  RootStateTypeExtra,
} from '@store/types'

import CarouselHandler from '@components/carousel-handler'
import CarouselOverlay from '@components/carousel-overlay'
import VideoPlayer from '@components/video-player'

import {
  FileInterface,
  GalleryInterface,
  LotAssetInterface,
  LotInterface,
  PackageInterface,
} from '@api/houseAndLand'

import useTabVisibility from '@utilities/tab-visibility'
import VideoPlayerFirebaseService from '@utilities/video-player-firebase-service'

import FirebaseControlQuery from '@firebaseUtil/firebase-control-query'
import { SessionMap } from '@firebaseUtil/types'

import { FileType } from '@adUtilities/constants/common'
import mapPropertyGallery, {
  FloorImage,
  PropertyGallery,
} from '@adUtilities/property-gallery'
import { ViewLineImage } from '@adUtilities/types'

import DeselectedView from './deselected-view'

export interface LotGalleryControlSessionInterface {
  galleryControl: GalleryControlInterface
  playerControl: PlayerControlInterface
}

interface LotGalleryProps {
  session: SessionMap | undefined
  lotGalleryData?: Array<LotGalleryInterface>
  lastActiveOptionType?: string
  lastActiveOptionId?: string
  setShouldShowSlideShowBtn: (arg: boolean) => void
  activePackageData?: PackageInterface
  activeLotData?: LotInterface
  packageImages: FileInterface[]
  projectIdentity: ProjectIdentity
}

const NO_IMAGE_OBJECT = {
  id: 'no-image',
  src: '',
  label: 'No Image',
  type: FileType.Image,
  noSpliceUrl: false,
}

function LotGallery({
  session,
  lotGalleryData,
  lastActiveOptionType,
  lastActiveOptionId,
  setShouldShowSlideShowBtn,
  activePackageData,
  activeLotData,
  packageImages,
  projectIdentity,
}: LotGalleryProps) {
  const firebaseControlQuery = FirebaseControlQuery({
    ...projectIdentity,
    sessionKey: projectIdentity.sessionId,
  })

  const videoPlayerFirebaseService = VideoPlayerFirebaseService({
    galleryName: 'propertyGallery',
    projectIdentity,
  })

  const [activeSlideIndex, setActiveSlideIndex] = useState(0)
  const [isBuffering, setBufferState] = useState(false)
  const [scrubbarTimerState, setScrubbarTimerState] = useState(false)
  const [playerState, setPlayerState] = useState<PlayerState>(PlayerState.Stop)
  const [isMuted, setMuteState] = useState(true)
  const [volume, setVolume] = useState<number>(0)
  const [videoLengthInSeconds, setVideoLengthInSeconds] = useState(0)
  const [currentVideoPositionInSeconds, setCurrentVideoPositionInSeconds] =
    useState(0)
  const [isSeeking, setSeekingState] = useState(false)

  const [showEnlargedView, setShowEnlargedView] = useState(false)

  const transformDataForGallery = (
    argData: Array<
      (FileInterface | GalleryInterface) & {
        noSpliceUrl?: boolean
        staticUrl?: boolean
      }
    >
  ): LotGalleryInterface[] =>
    argData
      .sort((a, b) => a.order - b.order)
      .map((item) => ({
        id: item.id,
        src: item.url,
        label: item.title || item.name,
        type: 'type' in item ? (item.type as FileType) : FileType.Image,
        thumbnail: 'thumbnail' in item ? (item.thumbnail as string) : '',
        noSpliceUrl: Boolean(item.noSpliceUrl),
        staticUrl: typeof item.staticUrl === 'boolean' ? item.staticUrl : true,
      }))

  const transformImageAsset = (item: LotAssetInterface) => ({
    id: item.id,
    src: item.url,
    label: item.title || item.name,
    order: item.order,
  })

  const lotGallery: LotGalleryInterface[] = useMemo(() => {
    if (!activePackageData) {
      const floorItems: FloorImage[] = activeLotData?.assets?.floorImage
        ? [transformImageAsset(activeLotData.assets.floorImage)]
        : []

      const viewLineItems: ViewLineImage[] =
        activeLotData?.assets?.viewLinesImage?.map(transformImageAsset) || []

      const propertyGalleries: PropertyGallery[] = (lotGalleryData || []).map(
        (item: LotGalleryInterface) => ({
          id: item.id,
          src: item.src,
          label: item.label || '',
          type: item.type,
          thumbnail: item.thumbnail,
          noSpliceUrl: false,
        })
      )

      return mapPropertyGallery({
        projectId: projectIdentity.projectUUID,
        floorImages: floorItems,
        viewLineImages: viewLineItems,
        propertyGalleries,
        onlyShowPropertyGallery:
          projectIdentity?.onlyShowPropertyGalleries || false,
      })
    }

    if (
      Object.values(HouseAndLandPackageOptionType).includes(
        lastActiveOptionType as HouseAndLandPackageOptionType
      )
    ) {
      if (!lastActiveOptionId) {
        return []
      }

      if (packageImages.length === 0) {
        return [NO_IMAGE_OBJECT]
      }

      return transformDataForGallery([...packageImages])
    }

    const packageGalleries = activePackageData?.galleries || []

    if (packageGalleries.length === 0) {
      return [NO_IMAGE_OBJECT]
    }

    return transformDataForGallery([...packageGalleries])
  }, [
    activePackageData,
    activeLotData,
    lastActiveOptionId,
    lastActiveOptionType,
    packageImages,
    projectIdentity.projectUUID,
    projectIdentity?.onlyShowPropertyGalleries,
  ])

  useEffect(() => {
    firebaseControlQuery.update({
      [`propertyGallery.galleryControlV2.activeSlideIndex`]: 0,
    })
  }, [lotGallery?.[0]?.id])

  useEffect(() => {
    setShouldShowSlideShowBtn(lotGallery.length > 1)
  }, [lotGallery.length])

  const galleryData: GalleryItemInterface[] = useMemo(() => {
    const galleryItems: Array<GalleryItemInterface> = []

    if (lotGallery && lotGallery.length > 0) {
      lotGallery.forEach((galleryItem) => {
        const imageSource =
          galleryItem.type === 'video'
            ? galleryItem.thumbnail || ''
            : galleryItem.src
        const videoSource = galleryItem.type === 'video' ? galleryItem.src : ''
        galleryItems.push({
          id: galleryItem.id,
          imageSource,
          videoSource,
          config: {
            staticUrl: galleryItem.staticUrl,
            noSpliceUrl: galleryItem.noSpliceUrl,
          },
        })
      })
    }

    return galleryItems
  }, [lotGallery])

  const hasVideo = useMemo(
    () => galleryData?.some((item) => !!item.videoSource),
    [galleryData]
  )

  const activeItem = useMemo(
    () => galleryData?.[activeSlideIndex],
    [galleryData, activeSlideIndex]
  )

  const handlePlayClick = useCallback(async () => {
    setPlayerState(PlayerState.Play)
    setScrubbarTimerState(false)
    await videoPlayerFirebaseService.updatePlayState()
  }, [])

  const handlePauseClick = useCallback(async () => {
    setPlayerState(PlayerState.Pause)
    setScrubbarTimerState(false)
    await videoPlayerFirebaseService.updatePauseState()
  }, [])

  const handleStopClick = useCallback(async () => {
    setScrubbarTimerState(false)
    setPlayerState(PlayerState.Stop)
    setVideoLengthInSeconds(0)
    setCurrentVideoPositionInSeconds(0)
    await videoPlayerFirebaseService.updateStopState()
  }, [])

  const handleSeekTo = useCallback(async (argInSeconds: number) => {
    setScrubbarTimerState(false)
    setSeekingState(true)
    await videoPlayerFirebaseService.updateSeekState(argInSeconds)
  }, [])

  const handleMuteClick = useCallback(async (arg: boolean) => {
    setMuteState(arg)
    await videoPlayerFirebaseService.updateMuteState(arg)
  }, [])

  const handleVolumeDownClick = useCallback(async () => {
    if (volume > 0) {
      const newVolume = volume - 1
      setMuteState(false)
      setVolume(newVolume)
      await videoPlayerFirebaseService.updateVolumeState(newVolume)
    }
  }, [volume])

  const handleVolumeUpClick = useCallback(async () => {
    if (volume < 10) {
      setMuteState(false)
      const newVolume = volume + 1
      setVolume(newVolume)
      await videoPlayerFirebaseService.updateVolumeState(newVolume)
    }
  }, [volume])

  const handleGalleryItemClick = useCallback(() => {
    setShowEnlargedView(true)
    firebaseControlQuery.update({
      [`propertyGallery.galleryControlV2.autoPlayState`]: false,
    })
  }, [firebaseControlQuery])

  const updateSyncState = useCallback(async () => {
    await videoPlayerFirebaseService.updateSyncState()
  }, [videoPlayerFirebaseService])

  useTabVisibility(
    useCallback(async () => {
      if (playerState === PlayerState.Play) {
        updateSyncState()
      }
    }, [playerState])
  )

  const handleChangeActiveItemIndex = useCallback(
    async (index: number) => {
      setActiveSlideIndex(index)
      setPlayerState(PlayerState.Stop)
      setScrubbarTimerState(false)
      await firebaseControlQuery.update({
        [`propertyGallery.galleryControlV2.autoPlayState`]: false,
        [`propertyGallery.galleryControlV2.activeSlideIndex`]: index,
        [`propertyGallery.playerControlV2.playerState`]: PlayerState.Stop,
        [`propertyGallery.playerControlV2.scrubbarTimerState`]: false,
        [`propertyGallery.playerControlV2.videoLengthInSeconds`]: 0,
        [`propertyGallery.playerControlV2.currentVideoPositionInSeconds`]: 0,
      })
    },
    [activeSlideIndex]
  )

  const handlePlayButtonClick = useCallback(async (index: number) => {
    setActiveSlideIndex(index)
    setPlayerState(PlayerState.Play)
    setScrubbarTimerState(false)
    await firebaseControlQuery.update({
      [`propertyGallery.galleryControlV2.autoPlayState`]: false,
      [`propertyGallery.galleryControlV2.activeSlideIndex`]: index,
      [`propertyGallery.playerControlV2.playerState`]: PlayerState.Play,
      [`propertyGallery.playerControlV2.scrubbarTimerState`]: false,
    })
  }, [])

  useEffect(() => {
    if (!session) {
      return
    }
    const {
      propertyGallery: {
        galleryControlV2: { activeSlideIndex: activeSlideIndexFirebase },
        playerControlV2: {
          playerState: playerStateFirebase,
          videoLengthInSeconds: videoLengthInSecondsFirebase,
          currentVideoPositionInSeconds: currentVideoPositionInSecondsFirebase,
          volume: volumeFirebase,
          scrubbarTimerState: scrubbarTimerStateFirebase,
          seekToValueInSeconds: seekToValueInSecondsFirebase,
          isBuffering: isBufferingFirebase,
          muted: muteStateFirebase,
        },
      },
    } = session
    setActiveSlideIndex(activeSlideIndexFirebase)
    setPlayerState(playerStateFirebase)
    setVideoLengthInSeconds(videoLengthInSecondsFirebase)
    setCurrentVideoPositionInSeconds(currentVideoPositionInSecondsFirebase)
    setVolume(volumeFirebase)
    setScrubbarTimerState(scrubbarTimerStateFirebase)
    setBufferState(isBufferingFirebase)
    setSeekingState(seekToValueInSecondsFirebase !== null)
    setMuteState(muteStateFirebase)
  }, [session?.propertyGallery])

  const enableEnlargedView = useMemo(
    () =>
      (galleryData || []).filter((item) => item.id !== NO_IMAGE_OBJECT.id)
        .length >= 1,
    [galleryData]
  )

  useEffect(() => {
    updateSyncState()
  }, [enableEnlargedView, showEnlargedView])

  return !!lotGallery && lotGallery.length > 0 ? (
    <>
      <CarouselHandler
        carousel={{
          label: '',
          items: galleryData || [],
        }}
        imgClass="object-contain"
        hasBlurredEffect
        activeIndex={activeSlideIndex}
        showPlay={playerState === PlayerState.Stop}
        onItemClick={handleGalleryItemClick}
        onChangeActiveItemIndex={handleChangeActiveItemIndex}
        onPlayClick={handlePlayButtonClick}
      />
      {!showEnlargedView && hasVideo ? (
        <div
          className={`absolute bottom-0 z-40 w-full pb-4 transition-all duration-300 ${
            playerState !== PlayerState.Stop
              ? 'translate-y-0 opacity-100'
              : 'pointer-events-none fixed translate-y-full opacity-0'
          }`}
        >
          <VideoPlayer
            scrubbarTimerState={scrubbarTimerState}
            isBuffering={isBuffering}
            isMuted={isMuted}
            volume={volume}
            isLooping={false}
            playerState={playerState}
            videoLengthInSeconds={videoLengthInSeconds}
            currentVideoPositionInSeconds={currentVideoPositionInSeconds}
            isSeeking={isSeeking}
            handleStopClick={handleStopClick}
            handlePauseClick={handlePauseClick}
            handlePlayClick={handlePlayClick}
            handleMuteClick={handleMuteClick}
            handleVolumeUpClick={handleVolumeUpClick}
            handleVolumeDownClick={handleVolumeDownClick}
            handleSeekTo={handleSeekTo}
            showLoopOption={false}
          />
        </div>
      ) : null}
      {enableEnlargedView && showEnlargedView ? (
        <CarouselOverlay
          carouselData={{
            index: activeSlideIndex,
            item: activeItem,
          }}
          carouselItems={galleryData || []}
          onItemClick={handleChangeActiveItemIndex}
          showPlay={playerState === PlayerState.Stop}
          onPlayClick={handlePlayButtonClick}
          showEnlargedView={showEnlargedView}
          onOverlayClick={() => setShowEnlargedView(false)}
        >
          {hasVideo ? (
            <div
              className={`absolute bottom-0 z-40 w-full px-20 pb-4 transition-all duration-300 ${
                playerState !== PlayerState.Stop
                  ? 'translate-y-0 opacity-100'
                  : 'pointer-events-none fixed translate-y-full opacity-0'
              }`}
            >
              <VideoPlayer
                scrubbarTimerState={scrubbarTimerState}
                isBuffering={isBuffering}
                isMuted={isMuted}
                volume={volume}
                isLooping={false}
                playerState={playerState}
                videoLengthInSeconds={videoLengthInSeconds}
                currentVideoPositionInSeconds={currentVideoPositionInSeconds}
                isSeeking={isSeeking}
                handleStopClick={handleStopClick}
                handlePauseClick={handlePauseClick}
                handlePlayClick={handlePlayClick}
                handleMuteClick={handleMuteClick}
                handleVolumeUpClick={handleVolumeUpClick}
                handleVolumeDownClick={handleVolumeDownClick}
                handleSeekTo={handleSeekTo}
                showLoopOption={false}
              />
            </div>
          ) : null}
        </CarouselOverlay>
      ) : null}
    </>
  ) : (
    <DeselectedView type={lastActiveOptionType || ''} />
  )
}
export default connect(
  ({ projectIdentity, firestore: { session } }: RootStateTypeExtra) => ({
    session,
    projectIdentity,
  })
)(LotGallery)
